import { getSessionItem, removeSessionItem, setSessionItem } from "@/utils/safeStorage";

export const SIGNUP_ANALYTICS_INTENT_KEY = "signupAnalyticsIntent";

export type SignupAnalyticsRole = "coach" | "leader";
export type SignupAnalyticsProvider = "email" | "google" | "microsoft";

export type SignupAnalyticsIntent = {
  signup_intent_id: string;
  role: SignupAnalyticsRole;
  auth_provider: SignupAnalyticsProvider;
  trial_account_mode?: string;
  created_at: string;
};

type SignupAnalyticsInput = {
  role: SignupAnalyticsRole;
  authProvider: SignupAnalyticsProvider;
  trialAccountMode?: string | null;
};

function createIntentId() {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `signup-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

function parseSignupAnalyticsIntent(raw: string | null): SignupAnalyticsIntent | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SignupAnalyticsIntent>;
    if (
      typeof parsed.signup_intent_id === "string" &&
      typeof parsed.role === "string" &&
      typeof parsed.auth_provider === "string" &&
      typeof parsed.created_at === "string" &&
      (parsed.role === "coach" || parsed.role === "leader") &&
      (parsed.auth_provider === "email" || parsed.auth_provider === "google" || parsed.auth_provider === "microsoft")
    ) {
      return {
        signup_intent_id: parsed.signup_intent_id,
        role: parsed.role,
        auth_provider: parsed.auth_provider,
        ...(typeof parsed.trial_account_mode === "string" && parsed.trial_account_mode
          ? { trial_account_mode: parsed.trial_account_mode }
          : {}),
        created_at: parsed.created_at,
      };
    }
  } catch {
    return null;
  }
  return null;
}

export function getSignupAnalyticsIntent(): SignupAnalyticsIntent | null {
  return parseSignupAnalyticsIntent(getSessionItem(SIGNUP_ANALYTICS_INTENT_KEY));
}

export function beginSignupAnalyticsIntent(input: SignupAnalyticsInput): SignupAnalyticsIntent {
  const existing = getSignupAnalyticsIntent();
  const trialAccountMode = input.trialAccountMode?.trim() || undefined;
  if (
    existing &&
    existing.role === input.role &&
    existing.auth_provider === input.authProvider &&
    (existing.trial_account_mode ?? undefined) === trialAccountMode
  ) {
    return existing;
  }

  const intent: SignupAnalyticsIntent = {
    signup_intent_id: createIntentId(),
    role: input.role,
    auth_provider: input.authProvider,
    ...(trialAccountMode ? { trial_account_mode: trialAccountMode } : {}),
    created_at: new Date().toISOString(),
  };
  setSessionItem(SIGNUP_ANALYTICS_INTENT_KEY, JSON.stringify(intent));
  return intent;
}

export function clearSignupAnalyticsIntent() {
  removeSessionItem(SIGNUP_ANALYTICS_INTENT_KEY);
}

export function signupAnalyticsEventProperties(intent: SignupAnalyticsIntent) {
  return {
    signup_intent_id: intent.signup_intent_id,
    auth_provider: intent.auth_provider,
    role: intent.role,
    ...(intent.trial_account_mode ? { trial_account_mode: intent.trial_account_mode } : {}),
  };
}
